import { CheckIcon, ChevronDownIcon, ChevronRightIcon } from "lucide-react";
import { Fragment, type KeyboardEvent, useEffect, useMemo, useRef, useState } from "react";
import { Button } from "@/components/ui/button";
import { Empty, EmptyDescription, EmptyHeader } from "@/components/ui/empty";
import { Field, FieldLabel } from "@/components/ui/field";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Separator } from "@/components/ui/separator";
import { Spinner } from "@/components/ui/spinner";
import { useI18n } from "@/locales/i18n-provider";
import { cn } from "@/utils";
import { type CascadingCategory, categorySelectionPath, categorySelectLevels, isSelectableCategory } from "./category-cascading-select-model";

type Props = {
  categories: readonly CascadingCategory[];
  value: string | null;
  onValueChange: (value: string) => void;
  id: string;
  disabled?: boolean;
  loading?: boolean;
  invalid?: boolean;
  descriptionId?: string;
  label: string;
  subcategoryLabel: string;
  placeholder: string;
  commonGroupLabel: string;
  projectGroupLabel: string;
};
type FocusedItem = { column: number; id: string | null };
const itemKey = ({ column, id }: FocusedItem) => `${column}:${id ?? ""}`;

/** One committed value, with an independent browsing path across the popup's columns. */
export function CategoryCascadingSelect({
  categories,
  value,
  onValueChange,
  id,
  disabled = false,
  loading = false,
  invalid = false,
  descriptionId,
  label,
  subcategoryLabel,
  placeholder,
  commonGroupLabel,
  projectGroupLabel,
}: Props) {
  const { t } = useI18n();
  const [open, setOpen] = useState(false);
  const [browsingValue, setBrowsingValue] = useState(value);
  const [focused, setFocused] = useState<FocusedItem>({ column: 0, id: null });
  const items = useRef(new Map<string, HTMLButtonElement>());
  const trigger = useRef<HTMLButtonElement>(null);
  const content = useRef<HTMLDivElement>(null);
  const tabbingOut = useRef(false);
  const blocked = disabled || loading;
  const visible = open && !blocked;
  const selectedPath = useMemo(() => categorySelectionPath(categories, value), [categories, value]);
  const levels = useMemo(() => categorySelectLevels(categories, browsingValue), [categories, browsingValue]);
  const parentIds = useMemo(() => new Set(categories.map((category) => category.parentId).filter(Boolean)), [categories]);
  const initialRootId = selectedPath[0]?.id ?? levels[0]?.options[0]?.id ?? null;

  useEffect(() => {
    if (blocked) setOpen(false);
  }, [blocked]);
  useEffect(() => {
    setBrowsingValue(value);
    setFocused({ column: 0, id: initialRootId });
  }, [initialRootId, value]);
  useEffect(() => {
    if (!visible) return;
    const button = items.current.get(itemKey(focused));
    button?.focus({ preventScroll: true });
    button?.scrollIntoView({ block: "nearest", inline: "nearest" });
  }, [focused, visible]);

  const choose = (next: string) => {
    if (blocked || !isSelectableCategory(categories, next)) return;
    setOpen(false);
    onValueChange(next);
  };
  const browse = (category: CascadingCategory, column: number, enter = false) => {
    setBrowsingValue(category.id);
    const firstChild = categories.find((item) => item.parentId === category.id);
    setFocused(enter && firstChild ? { column: column + 1, id: firstChild.id } : { column, id: category.id });
  };
  const handleKey = (event: KeyboardEvent<HTMLButtonElement>, column: number, category: CascadingCategory) => {
    const level = levels[column];
    if (!level) return;
    const ordered = level.parent ? level.options : [...level.options.filter((item) => !item.projectId), ...level.options.filter((item) => item.projectId)];
    const options = ordered.map((item) => item.id);
    if (options.length === 0) return;
    const index = options.indexOf(category.id);
    if (event.key === "ArrowDown" || event.key === "ArrowUp" || event.key === "Home" || event.key === "End") {
      event.preventDefault();
      const next =
        event.key === "Home" ? 0 : event.key === "End" ? options.length - 1 : (index + (event.key === "ArrowDown" ? 1 : -1) + options.length) % options.length;
      setFocused({ column, id: options[next] ?? null });
    } else if (event.key === "ArrowRight" && parentIds.has(category.id)) {
      event.preventDefault();
      browse(category, column, true);
    } else if (event.key === "ArrowLeft" && level.parent) {
      event.preventDefault();
      setBrowsingValue(level.parent.id);
      setFocused({ column: column - 1, id: level.parent.id });
    } else if (event.key === "Tab") {
      tabbingOut.current = true;
      trigger.current?.focus();
      setOpen(false);
    }
  };

  const renderItem = (column: number, category: CascadingCategory, text: string) => {
    const level = levels[column];
    const nextValue = category.id;
    const branch = parentIds.has(category.id);
    const active = level?.value === category.id;
    const selected = !branch && value === nextValue;
    const key = itemKey({ column, id: category.id });
    return (
      <Button
        aria-checked={branch ? undefined : selected}
        aria-controls={branch ? `${id}-column-${column + 1}` : undefined}
        aria-expanded={branch ? active : undefined}
        aria-haspopup={branch ? "menu" : undefined}
        className={cn("h-8 w-full justify-start rounded-md px-2 text-left font-normal", active && "bg-muted", selected && "text-primary")}
        key={key}
        onClick={() => (branch ? browse(category, column, true) : choose(nextValue))}
        onKeyDown={(event) => handleKey(event, column, category)}
        onPointerEnter={(event) => {
          if (event.pointerType === "mouse") browse(category, column);
        }}
        ref={(button) => {
          if (button) items.current.set(key, button);
          else items.current.delete(key);
        }}
        role={branch ? "menuitem" : "menuitemradio"}
        tabIndex={itemKey(focused) === key ? 0 : -1}
        title={text}
        type="button"
        variant="ghost"
      >
        <span className="min-w-0 flex-1 truncate">{text}</span>
        {selected ? <CheckIcon aria-hidden="true" data-icon="inline-end" /> : null}
        {branch ? <ChevronRightIcon aria-hidden="true" data-icon="inline-end" /> : null}
      </Button>
    );
  };

  return (
    <Field data-disabled={blocked} data-invalid={invalid}>
      <FieldLabel htmlFor={id}>
        {label} <span aria-hidden="true">*</span>
      </FieldLabel>
      <Popover
        open={visible}
        onOpenChange={(next) => {
          if (blocked) return;
          setBrowsingValue(value);
          setFocused({ column: 0, id: initialRootId });
          setOpen(next);
        }}
      >
        <PopoverTrigger asChild>
          <Button
            aria-busy={loading || undefined}
            aria-describedby={[`${id}-value`, descriptionId].filter(Boolean).join(" ")}
            aria-invalid={invalid}
            aria-required="true"
            className="h-9 w-full min-w-0 justify-between px-3 font-normal"
            disabled={blocked}
            id={id}
            ref={trigger}
            role="combobox"
            title={selectedPath.length ? selectedPath.map((category) => category.name).join(" / ") : placeholder}
            type="button"
            variant="outline"
          >
            <span className={cn("min-w-0 flex-1 truncate text-left", selectedPath.length === 0 && "text-muted-foreground")} id={`${id}-value`}>
              {selectedPath.length ? selectedPath.map((category) => category.name).join(" / ") : placeholder}
            </span>
            {loading ? <Spinner data-icon="inline-end" /> : <ChevronDownIcon aria-hidden="true" data-icon="inline-end" />}
          </Button>
        </PopoverTrigger>
        <PopoverContent
          align="start"
          aria-label={label}
          className="max-h-(--radix-popover-content-available-height) w-max max-w-(--radix-popover-content-available-width) gap-0 overflow-hidden p-0"
          collisionPadding={8}
          onKeyDown={(event) => {
            if (event.key === "Tab" && initialRootId === null) {
              tabbingOut.current = true;
              trigger.current?.focus();
              setOpen(false);
            }
          }}
          onCloseAutoFocus={(event) => {
            if (tabbingOut.current) event.preventDefault();
            tabbingOut.current = false;
          }}
          onOpenAutoFocus={(event) => {
            event.preventDefault();
            const first = initialRootId ? items.current.get(itemKey({ column: 0, id: initialRootId })) : content.current;
            first?.focus({ preventScroll: true });
          }}
          ref={content}
          tabIndex={-1}
        >
          <div className="flex min-h-0 max-h-72 max-w-full overflow-x-auto overscroll-x-contain">
            {levels.map((level, column) => {
              const groups = level.parent
                ? [{ key: level.parent.id, label: level.parent.name, options: level.options }]
                : [
                    { key: "common", label: commonGroupLabel, options: level.options.filter((category) => !category.projectId) },
                    { key: "project", label: projectGroupLabel, options: level.options.filter((category) => category.projectId) },
                  ];
              return (
                <Fragment key={level.parent?.id ?? "root"}>
                  {column > 0 ? <Separator orientation="vertical" /> : null}
                  <ScrollArea className="flex min-h-0 w-52 shrink-0 flex-col [&>[data-slot=scroll-area-viewport]]:min-h-0">
                    <div
                      aria-label={level.parent ? `${subcategoryLabel}: ${level.parent.path}` : label}
                      className="flex flex-col gap-1 p-1.5"
                      id={`${id}-column-${column}`}
                      role="menu"
                    >
                      {groups.map((group) =>
                        group.options.length ? (
                          // biome-ignore lint/a11y/useSemanticElements: This groups menu items, not form controls.
                          <div aria-labelledby={`${id}-group-${group.key}`} className="flex flex-col gap-0.5" key={group.key} role="group">
                            <span className="px-2 py-1 text-xs text-muted-foreground" id={`${id}-group-${group.key}`}>
                              {group.label}
                            </span>
                            {group.options.map((category) => renderItem(column, category, category.name))}
                          </div>
                        ) : null,
                      )}
                      {level.options.length === 0 ? (
                        <Empty className="gap-2 px-2 py-6">
                          <EmptyHeader>
                            <EmptyDescription>{t("categorySelect.noMatches")}</EmptyDescription>
                          </EmptyHeader>
                        </Empty>
                      ) : null}
                    </div>
                  </ScrollArea>
                </Fragment>
              );
            })}
          </div>
        </PopoverContent>
      </Popover>
    </Field>
  );
}
